import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

// Custom hook for debouncing
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    const timeoutRef = useRef(null);

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [value, delay]);

    return debouncedValue;
}

// Utility function to truncate strings
const truncateString = (str, maxLength) => {
    if (str.length > maxLength) {
        return str.substring(0, maxLength) + '...';
    }
    return str;
};

// Format date to show as "MMM DD, YYYY"
const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

function ManagerPlayerListPage() {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedSeason, setSelectedSeason] = useState('all');
    const [activeSeasons, setActiveSeasons] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const searchTimeoutRef = useRef(null);

    const fetchPlayers = useCallback(async (search, status = filterStatus) => {
        try {
            setSearchLoading(true);
            const response = await axios.get('/api/player/', {
                params: {
                    search: search,
                    status: status,
                    season: selectedSeason === 'all' ? undefined : selectedSeason,
                    page: currentPage,
                    page_size: pageSize,
                },
                withCredentials: true,
            });

            if (response.data.results) {
                setPlayers(response.data.results);
                setTotalPages(response.data.total_pages || Math.ceil(response.data.count / pageSize));
                setTotalCount(response.data.total_count || response.data.count);
                if (response.data.active_seasons) {
                    setActiveSeasons(response.data.active_seasons);
                    if (isInitialLoad && response.data.active_seasons.length > 0) {
                        setSelectedSeason(response.data.active_seasons[0].id.toString());
                        setIsInitialLoad(false);
                    }
                }
            } else {
                setPlayers(response.data);
                setTotalPages(1);
                setTotalCount(response.data.length);
            }
            setError(null);
        } catch (error) {
            setError('Failed to load players');
        } finally {
            setSearchLoading(false);
            setLoading(false);
        }
    }, [selectedSeason, currentPage, pageSize, isInitialLoad]);

    // Initial load
    useEffect(() => {
        setLoading(true);
        fetchPlayers('');
    }, [fetchPlayers]);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setCurrentPage(1);

        // Clear any existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout
        searchTimeoutRef.current = setTimeout(() => {
            fetchPlayers(value);
        }, 300);
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const handleFilterChange = (e) => {
        const newStatus = e.target.value;
        setFilterStatus(newStatus);
        setCurrentPage(1);
        fetchPlayers(searchTerm, newStatus);
    };

    const handleSeasonChange = (e) => {
        setSelectedSeason(e.target.value);
        setCurrentPage(1);
        fetchPlayers(searchTerm);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
        fetchPlayers(searchTerm);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        fetchPlayers(searchTerm);
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header border-0 pb-0">
                <h5 className="card-title">Players</h5>
                <p className="mb-0">View and manage all players here.</p>
            </div>
            <div className="card-body">
                <div className="row mb-4">
                    <div className="col-md-4">
                        <div className="input-group">
                            <span className="input-group-text">
                                <i className="bi bi-search"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search players..."
                                value={searchTerm}
                                onChange={handleSearch}
                                autoComplete="off"
                            />
                            {searchLoading && (
                                <span className="input-group-text">
                                    <div className="spinner-border spinner-border-sm" role="status">
                                        <span className="visually-hidden">Searching...</span>
                                    </div>
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-select"
                            value={filterStatus}
                            onChange={handleFilterChange}
                        >
                            <option value="all">All Players</option>
                            <option value="available">Available Only</option>
                            <option value="on_team">On Team</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={selectedSeason}
                            onChange={handleSeasonChange}
                        >
                            <option value="all">All Seasons</option>
                            {activeSeasons.map((season) => (
                                <option key={season.id} value={season.id}>
                                    {season.name} ({season.league__name}) - Due {formatDate(season.registration_deadline)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        >
                            <option value="5">5 per page</option>
                            <option value="10">10 per page</option>
                            <option value="20">20 per page</option>
                            <option value="50">50 per page</option>
                            <option value="100">100 per page</option>
                        </select>
                    </div>
                </div>

                <div className="row g-4">
                    {players.map((player) => (
                        <div key={player.id} className="col-sm-6 col-lg-4">
                            <div className="card h-100">
                                <div className="card-body">
                                    <div className="d-flex flex-column">
                                        <div className="d-flex align-items-center mb-3">
                                            <img
                                                src={player.profile_picture || `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`}
                                                alt={`${player.first_name} ${player.last_name}`}
                                                className="rounded-circle me-3"
                                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                            />
                                            <div className="flex-grow-1">
                                                <h6 className="mb-1">
                                                    <Link to={`/league-manager/players/${player.user.id}/`} className="text-decoration-none">
                                                        {truncateString(player.first_name + ' ' + player.last_name, 20)}
                                                    </Link>
                                                </h6>
                                                <small className="text-muted">{truncateString(player.email, 20)}</small>
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            <span className={`badge ${player.team ? 'bg-primary' : 'bg-success'}`}>
                                                {player.team ? `Team: ${player.team.name}` : 'Available'}
                                            </span>
                                            {player.seasons && player.seasons.length > 0 && (
                                                <div className="mt-2">
                                                    {player.seasons.map((season) => (
                                                        <span key={season.id} className="badge bg-info me-1">
                                                            {season.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {player.waiver_status && (
                                                <div className="mt-2">
                                                    <span className={`badge ${player.waiver_status.has_waiver ? 'bg-success' : 'bg-warning'}`}>
                                                        {player.waiver_status.has_waiver ? 'Waiver Signed' : 'Waiver Pending'}
                                                    </span>
                                                </div>
                                            )}
                                            {!player.paid && (
                                                <div className="mt-2">
                                                    <span className="badge bg-warning">Unpaid</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {players.length === 0 && (
                        <div className="col-12">
                            <div className="text-center text-muted py-4">
                                No players found
                            </div>
                        </div>
                    )}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div>
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} players
                        </div>
                        <nav aria-label="Page navigation">
                            <ul className="pagination mb-0">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                </li>
                                {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                                    // Show at most 5 page numbers
                                    const pageNum = index + 1;
                                    return (
                                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        </li>
                                    );
                                })}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ManagerPlayerListPage; 